<template>
  <!-- 添加 loading 遮罩 -->
  <!-- <div v-if="isLoading" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
    <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
  </div> -->
  <div class="min-h-screen bg-gray-50 p-4 md:p-8" data-report-root>
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- 顶部导航栏 -->
  <header class="bg-gradient-to-r from-blue-600 to-blue-800 shadow-lg">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center py-6">
        <h1 class="text-2xl font-bold text-white">{{ t('sleep_analysis_report') }}</h1>
        <div v-if="stage === 1">
          <span class="text-white/80 text-sm">{{ formatChineseDate(selectedDate) }}</span>
        </div> 
        <div v-if="stage === 2">
          <div class="flex items-center space-x-2">
            <span class="text-white/80 text-sm">{{ t('select_date_to_view') }}</span>
          <DatePicker
            v-model="selectedDate"
            :min-date="minDate"
            :max-date="maxDate"
            :attributes="attributes"
            :disabled-dates="disabledDates"
            :model-config="modelConfig"
            color="blue"
            is-expanded
            @dayclick="onDayClick"
            class="custom-calendar"
          >
            <template #default="{ inputValue, togglePopover }">
              <button
                class="flex items-center px-4 py-2 rounded-lg bg-white/20 text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/40 transition-all space-x-2"
                @click="togglePopover"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{{ inputValue || t('select_date') }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </template>
          </DatePicker>
        </div>
        </div>
      </div>
    </div>
  </header>

      <!-- Tab 切换 -->
      <div class="bg-white rounded-lg shadow-md border border-gray-100 mb-4 sticky top-0 z-10">
        <div class="flex border-b border-gray-200">
          <button
            type="button"
            @click="activeTab = 'report'"
            :class="[
              'px-6 py-3 text-sm font-medium transition-colors cursor-pointer',
              activeTab === 'report'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            ]"
          >
            {{ t('report_detail') }}
          </button>
          <button
            v-if="!isResearchRestricted"
            type="button"
            @click="taroDialogVisible = true"
            :class="[
              'px-6 py-3 text-sm font-medium transition-colors cursor-pointer',
              'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
            ]"
          >
            {{ t('taro_view') }}
          </button>
          <!-- 原始数据 tab 暂时隐藏 -->
          <button
            v-if="false"
            type="button"
            @click="activeTab = 'waveform'"
            :class="[
              'px-6 py-3 text-sm font-medium transition-colors cursor-pointer',
              activeTab === 'waveform'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            ]"
          >
            {{ t('raw_data') }}
          </button>
        </div>
      </div>

      <SubjectClinicalDocuments v-if="cohortOrg === '河南省人民医院' && cohortSubjectId" :key="JSON.stringify([cohortOrg, cohortSubjectId, clinicalSite])" :org="cohortOrg" :subject-id="cohortSubjectId" :site="clinicalSite" />

      <!-- 报告详情 Tab 内容 -->
      <div v-show="activeTab === 'report'">
      <div v-if="hasNapData" class="nap-tab-toggle">
        <button
          type="button"
          :class="{ active: reportTab === 'night' }"
          @click="reportTab = 'night'"
        >
          整夜报告
        </button>
        <button
          type="button"
          :class="{ active: reportTab === 'nap' }"
          @click="reportTab = 'nap'"
        >
          小睡报告
        </button>
      </div>

      <div v-show="reportTab === 'night' || !hasNapData">
      <!-- User Info Card -->
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100">
        <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h2 class="flex items-center text-xl font-bold text-gray-900"><span v-if="!cohortContext" class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>{{ t('basic_info') }}</h2>
          <button v-if="canResearchEdit && cohortContext" type="button" class="rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-600 disabled:opacity-40" :disabled="profileLoading || !cohortProfile || profileSaving" @click="showBasicInfoEditor = true">{{ t('cohort_profile_add') }}</button>
        </div>
        <p v-if="cohortContext && profileLoading" class="mb-4 text-sm text-gray-500">{{ t('cohort_profile_loading') }}</p>
        <div v-if="cohortContext && (profileError || !cohortSubjectId)" class="mb-4 text-sm text-red-600" role="alert">{{ profileError ? t(profileError) : t('cohort_profile_missing_context') }} <button v-if="cohortSubjectId && !profileSaving" type="button" class="ml-2 underline" @click="refreshProfile">{{ t('cohort_profile_retry') }}</button></div>
        
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- 基础信息 -->
          <template v-for="(item, index) in baseInfoItems" :key="'base-' + index">
            <div class="bg-gray-50 rounded-xl p-4 hover:bg-gray-100/80 transition-all duration-300">
              <div class="flex flex-col items-center">
                <div class="text-sm text-gray-500 mb-1 text-center">{{ item.label }}</div>
                <div class="font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-300 text-center">
                  {{ item.value }}
                </div>
              </div>
            </div>
          </template>

          <!-- 自定义字段 -->
          <template v-for="(field, key) in customFields" :key="'custom-' + key">
            <div class="bg-gray-50 rounded-xl p-4 hover:bg-gray-100/80 transition-all duration-300">
              <div class="flex flex-col items-center">
                <div class="text-sm text-gray-500 mb-1 text-center">{{ key }}</div>
                <div class="font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-300 text-center">
                  {{ field.value }} {{ field.unit }}
                </div>
              </div>
            </div>
          </template>
        </div>
        <div v-if="cohortContext" class="mt-4 rounded-lg bg-gray-50 p-4"><div class="mb-1 text-sm text-gray-500">{{ t('remarks') }}</div><p class="whitespace-pre-wrap break-words text-sm text-gray-900">{{ cohortProfile?.remarks ?? '--' }}</p></div>
        <SubjectBasicInfoEditor v-if="showBasicInfoEditor && cohortProfile" :key="`${cohortOrg}:${cohortSubjectId}:${profileContext().asOf}`" :info="cohortProfile" :subject-id="cohortSubjectId" :saving="profileSaving" :error="profileError" @close="showBasicInfoEditor = false" @save="saveBasicInfo" />
      </div>

      <!-- Sleep Analysis -->
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('sleep_analysis') }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-5 gap-2">
          <!-- 遍历所有睡眠分析指标 -->
          <div v-for="(item, index) in computedSleepAnalysis" 
               :key="index"
               class="flex flex-col p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-center"
          >
            <span class="text-sm text-gray-500 mb-1">{{ item.label }}</span>
            <span class="text-base font-semibold text-blue-600">{{ item.value }}</span>
          </div>

          <!-- ESS 评分 -->
          <div v-if="essScore !== null" class="flex flex-col p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-center">
            <span class="text-sm text-gray-500 mb-1">{{ t('ess_score') }}</span>
            <span class="text-base font-semibold text-blue-600">{{ essScore }}</span>
          </div>

          <!-- PSQI 评分 -->
          <div v-if="psqiScore !== null" class="flex flex-col p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-center">
            <span class="text-sm text-gray-500 mb-1">{{ t('psqi_score') }}</span>
            <span class="text-base font-semibold text-blue-600">{{ psqiScore }}</span>
          </div>
        </div>
      </div>

      <!-- Sleep Structure -->
      <div class="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('sleep_structure') }}
        </h2>

        <div class="flex flex-col md:flex-row gap-4">
          <!-- 左侧睡眠阶段统计 -->
          <div class="w-full md:w-1/4">
            <div class="grid grid-cols-1 gap-3 h-full">
              <div v-for="(phase, index) in computedSleepPhases" 
                   :key="index"
                   class="flex-1 flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-all"
                   :class="{
                     'border-l-[3px] border-l-orange-400': index === 0,
                     'border-l-[3px] border-l-blue-300': index === 1,
                     'border-l-[3px] border-l-blue-500': index === 2,
                     'border-l-[3px] border-l-blue-700': index === 3
                   }"
              >
                <div class="flex items-center justify-between w-full">
                  <span class="text-sm text-gray-500">{{ phase.label }}</span>
                  <div class="flex items-center gap-2">
                    <span class="text-base font-medium text-gray-900">{{ phase.value }}</span>
                    <span class="text-xs text-gray-500">{{ phase.percentage }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧图表区域 -->
          <div class="w-full md:w-3/4 flex flex-col gap-3">
            <!-- 睡眠结构图表 -->
            <div class="flex-1 bg-white rounded-lg border border-gray-100">
              <div class="h-[140px] relative">
                <div :id="chartId" class="w-full h-full"></div>
                <div v-if="!data" class="absolute inset-0 flex items-center justify-center bg-gray-50/80">
                  <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                </div>
              </div>
            </div>

            <!-- 其他图表 -->
            <div class="flex-1 bg-white rounded-lg border border-gray-100">
              <div class="h-[140px] relative">
                <div :id="ArtChartId" class="w-full h-full"></div>
                <div v-if="!data" class="absolute inset-0 flex items-center justify-center bg-gray-50/80">
                  <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Breathing Health -->
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('respiratory_health') }}
        </h2>
        
        <div class="flex flex-col md:flex-row gap-4">
          <!-- 左侧统计信息 -->
          <div class="w-full md:w-1/4">
            <div class="grid grid-cols-1 gap-2">
              <div v-for="(item, index) in computedBreathingHealth" 
                   :key="index"
                   class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-all"
              >
                <span class="text-sm text-gray-500">{{ item.label }}</span>
                <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
              </div>
            </div>
          </div>

          <!-- 右侧图表区域 -->
          <div class="w-full md:w-3/4 space-y-2">
            <!-- 呼吸暂停事件图表 -->
            <div class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 pt-2 text-gray-500">{{ isOAHIDevice ? t('whole_night_oahi_events_full') : t('whole_night_ahi_events_full') }}</h3>
              <div class="h-[140px]">
                <div :id="AhChartId" class="w-full h-full"></div>
              </div>
            </div>

            <!-- 鼾声分析图表 -->
            <div class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 pt-2 text-gray-500">{{ t('snore_analysis') }}</h3>
              <div class="h-[140px]">
                <div :id="SnoreChartId" class="w-full h-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sleep Period Indicators & Charts -->
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('sleep_physiology_metrics') }}
        </h2>
        
        <div class="space-y-4">
          <!-- 指标卡片 -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div v-for="(item, index) in computedSleepPeriodIndicators" 
                 :key="index"
                 class="flex flex-col p-2.5 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-center"
            >
              <span class="text-sm text-gray-500 mb-1">{{ item.label }}</span>
              <div class="flex items-center justify-center gap-1">
                <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
                <span v-if="item.trend" 
                      class="text-xs"
                      :class="{
                        'text-green-500': item.trend > 0,
                        'text-red-500': item.trend < 0,
                        'text-gray-400': item.trend === 0
                      }"
                >
                  {{ item.trend > 0 ? '↑' : item.trend < 0 ? '↓' : '–' }}
                </span>
              </div>
            </div>
          </div>

          <!-- 图表区域 -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- 睡眠心率变异性 -->
            <div class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_hrv') }}</h3>
              <div class="h-[140px] p-2">
                <div :id="HrvChartId" class="w-full h-full"></div>
              </div>
            </div>

            <!-- 睡眠呼吸频率 -->
            <div class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_respiratory_rate') }}</h3>
              <div class="h-[140px] p-2">
                <div :id="RrChartId" class="w-full h-full"></div>
              </div>
            </div>

            <!-- 睡眠心率 -->
            <div class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_heart_rate') }}</h3>
              <div class="h-[140px] p-2">
                <div :id="HrChartId" class="w-full h-full"></div>
              </div>
            </div>

            <!-- 血氧 (按夏祯 2026-05-11 要求隐藏) -->
            <div v-if="false" class="bg-white rounded-lg border border-gray-100">
              <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('spo2_t') }}</h3>
              <div class="h-[140px] p-2">
                <div :id="spo2ChartId" class="w-full h-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Sleep Environment -->
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('sleep_environment') }}
        </h2>
        
        <div class="flex flex-col md:flex-row gap-4">
          <!-- 左侧环境指标 -->
          <div class="w-full md:w-1/3">
            <div class="grid grid-cols-1 gap-2">
              <div v-for="(item, index) in computedSleepEnv" 
                   :key="index"
                   class="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
              >
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-500">{{ item.label }}</span>
                  <span v-if="item.description" 
                        class="text-xs text-gray-400 hidden md:inline"
                        :title="item.description"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                </div>
                <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
              </div>
            </div>
          </div>

          <!-- 右侧图表 -->
          <div class="w-full md:w-2/3">
            <div class="bg-white rounded-lg border border-gray-100">
              <div class="h-[140px]">
                <div :id="EnvChartId" class="w-full h-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
 
      <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100">
        <h2 class="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
          {{ t('monthly_trend') }}
        </h2>
        
        <!-- 使用grid布局将图表分为 -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="trend-chart-container min-w-0" data-health-trend="sleepAge">
            <h5 class="text-lg font-semibold mb-4">{{ t('research_trend_sleep_age') }}</h5>
            <div class="h-48">
              <div :id="sleepAgeChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_sleep_age')"></div>
            </div>
          </div>

          <div class="trend-chart-container min-w-0" data-health-trend="agingPace">
            <h5 class="text-lg font-semibold mb-4">{{ t('research_trend_aging_speed') }}</h5>
            <div class="h-48">
              <div :id="agingPaceChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_aging_speed')"></div>
            </div>
          </div>

          <div class="trend-chart-container min-w-0" data-health-trend="recovery">
            <h5 class="text-lg font-semibold mb-4">{{ t('research_trend_recovery') }}</h5>
            <div class="h-48">
              <div :id="recoveryChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_recovery')"></div>
            </div>
          </div>

          <!-- 总睡眠时间 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('total_sleep_time_t') }}</h5>
            <div class="h-48">
              <div :id=TstChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡速 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_onset_speed') }}</h5>
            <div class="h-48">
              <div :id=solChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠规律性 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_regularity') }}</h5>
            <div class="h-48">
              <div :id=sriChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠中断时长 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_interruption_duration') }}</h5>
            <div class="h-48">
              <div :id=wasoChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠效率 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_efficiency_t') }}</h5>
            <div class="h-48">
              <div :id=seChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 快速眼动和深睡时长 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('rem_and_deep_duration_t') }}</h5>
            <div class="h-48">
              <div :id=deepChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 各睡眠阶段的时长 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_stages_duration_title') }}</h5>
            <div class="h-48">
              <div :id=StageCompChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 呼吸暂停低通指数 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ isOAHIDevice ? t('obstructive_short') : '' }}{{ t('ahi_full_label') }} ({{ isOAHIDevice ? 'OAHI' : 'AHI' }})</h5>
            <div class="h-48">
              <div :id=ahiChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 鼾声指数 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('snore_index_alt') }}</h5>
            <div class="h-48">
              <div :id=SnoreIndexChartId class="w-full h-full"></div>
            </div>
          </div>
          
          <!-- 睡眠心率变异性 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_hrv') }}</h5>
            <div class="h-48">
              <div :id=hrvBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠呼吸频率 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_respiratory_rate') }}</h5>
            <div class="h-48">
              <div :id=rrBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠心 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_heart_rate') }}</h5>
            <div class="h-48">
              <div :id=hrBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠环境 -->
          <div class="trend-chart-container">
            <h5 class="text-lg font-semibold mb-4">{{ t('sleep_environment') }}</h5>
            <div class="h-48">
              <div :id=envChartId class="w-full h-full"></div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div v-show="reportTab === 'nap' && hasNapData" class="nap-report-list">
        <article
          v-for="nap in napReports"
          :key="`nap-${nap.index}`"
          class="nap-report-card"
        >
          <div class="nap-card-header">
            <div>
              <h2>小睡 {{ nap.index + 1 }}</h2>
              <p>时间段：{{ nap.timeRange }}</p>
            </div>
            <div class="nap-duration">
              <span>睡眠时长</span>
              <strong>{{ nap.sleepDuration }}</strong>
            </div>
          </div>

          <div class="nap-stage-section">
            <div class="nap-section-title">分期占比</div>
            <div class="nap-stage-bar" aria-label="小睡分期占比">
              <span
                v-for="stageItem in nap.stages"
                :key="stageItem.key"
                :style="{ width: `${stageItem.percentage}%`, backgroundColor: stageItem.color }"
              ></span>
            </div>
            <div class="nap-stage-legend">
              <div v-for="stageItem in nap.stages" :key="`${stageItem.key}-legend`">
                <span class="nap-stage-dot" :style="{ backgroundColor: stageItem.color }"></span>
                <span>{{ stageItem.label }}</span>
                <strong>{{ Math.round(stageItem.percentage) }}%</strong>
              </div>
            </div>
          </div>

          <div v-if="nap.hr || nap.hrv || nap.resp" class="nap-metrics">
            <div v-if="nap.hr" class="nap-metric-item">
              <span>心率（最低 / 平均）</span>
              <strong>{{ nap.hr }}</strong>
            </div>
            <div v-if="nap.hrv" class="nap-metric-item">
              <span>HRV</span>
              <strong>{{ nap.hrv }}</strong>
            </div>
            <div v-if="nap.resp" class="nap-metric-item">
              <span>呼吸频率</span>
              <strong>{{ nap.resp }}</strong>
            </div>
          </div>
        </article>
      </div>
      </div>

      <!-- 移动端视图 Dialog (点 Tab 按钮打开, 独立于外层容器, 不用跟 h-[calc(100vh-320px)] 斗) -->
      <el-dialog
        v-if="!isResearchRestricted"
        v-model="taroDialogVisible"
        :title="t('taro_view')"
        width="440px"
        align-center
        :destroy-on-close="true"
        append-to-body
      >
        <div v-if="mobileReportSessionMissing" class="text-center p-4 text-red-600" role="alert">{{ t('mobile_report_session_missing') }}</div>
        <div v-else-if="!taroIframeUrl" class="text-center p-4 text-gray-500" role="alert">{{ t('please_select_valid_date_raw') }}</div>
        <template v-else-if="taroDialogVisible">
          <div class="flex justify-center">
            <div class="phone-frame-dialog">
              <div class="phone-notch" />
              <div class="phone-screen">
                <iframe
                  ref="taroIframe"
                  :key="mobileReportContext.key"
                  :src="taroIframeUrl"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allow="clipboard-write"
                />
              </div>
            </div>
          </div>
        </template>
      </el-dialog>

      <!-- 原始数据 Tab 内容（暂时隐藏） -->
      <div v-if="false" class="bg-white rounded-lg shadow-md border border-gray-100 p-4">
        <DeviceWaveform
          v-if="formattedDateForWaveform"
          :device="selectedDevice"
          :date="formattedDateForWaveform"
          mode="device"
          server="收集"
        />
        <div v-else class="text-center text-gray-500 p-8">
          {{ t('please_select_valid_date_raw') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { alignMonthlyTrendAxis, monthlyTrendKeys } from '@/plugins/chart/monthly_trend_axis.js'
import { canResearchEdit, isResearchRestricted } from '@/modules/cohort/researchPermissions.js'

import { reportCalendarDate } from "@/plugins/orgDashboardApi.js";
import {onMounted, ref, watch, computed, onBeforeUnmount, nextTick} from 'vue'
import { t } from '@/plugins/utils/i18n';
import SubjectClinicalDocuments from '@/modules/cohort/components/SubjectClinicalDocuments.vue'
import { selectedCohortState } from '@/modules/cohort/cohortApi.js'
import SubjectBasicInfoEditor from '@/modules/cohort/components/SubjectBasicInfoEditor.vue'
import { useSubjectBasicInfo } from '@/modules/cohort/useSubjectBasicInfo.js'
import { basicInfoItems } from '@/modules/cohort/basicInfoModel.js'
import { currentLocale } from '@/plugins/utils/i18n';
import { setDisplayTimezone } from '@/plugins/utils/timezone'
import {createAxiosInstance, wdb_get_history_new} from "@/plugins/axios.js";
import { getMinVisibleDateForCurrentUser } from '@/config/orgAccessConfig';

import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import {request_data, fetchUserProfile as fetchUserProfileByPhone} from "@/plugins/axios.js";
import * as echarts from 'echarts'
import { buildHealthTrendDays, buildSleepAgeTrendOption, buildAgingPaceTrendOption,
  buildRecoveryScoreTrendOption } from '@/plugins/chart/research_health_trends.js'
import {setStageChartOption} from "@/plugins/chart/stage_chart";
import {setArtChartOption} from "@/plugins/chart/day_art_chart";
import {setArt2ChartOption} from "@/plugins/chart/day_art_chart2";
import {setDayEnvChartOption} from "@/plugins/chart/day_env_chart";
import {setDayHRVChartOption} from "@/plugins/chart/day_hrv_chart";
import {setDayRRChartOption} from "@/plugins/chart/day_rr_chart";
import {setStageAHChartOption} from "@/plugins/chart/stage_ah_chart";
import {setDayHRChartOption} from "@/plugins/chart/day_hr_chart";
import {setStageAH2ChartOption} from "@/plugins/chart/stage_ah_chart2";
import {chart_dates, resetDates} from "@/plugins/chart/variables";
import {formatDateString} from "@/plugins/utils/utils";
import {setSpO2ChartOption} from "@/plugins/chart/spo2_chart";
import {setDaySnoreChartOption} from "@/plugins/chart/day_snore_chart";
import {setTSTChartOption} from "@/plugins/chart/tst_chart";
import {setStageCompChartOption} from "@/plugins/chart/stage_comp_chart";
import {setSnoreIndexChartOption} from "@/plugins/chart/snore_index_chart";
import {setSOLChartOption} from "@/plugins/chart/sol_chart";
import {setWASOChartOption} from "@/plugins/chart/waso";
import {setDeepChartOption} from "@/plugins/chart/deep_time_chart";
import {setEnvChartOption} from "@/plugins/chart/env_chart";
import {setSEChartOption} from "@/plugins/chart/tst_chart";
import {setTIBChartOption} from "@/plugins/chart/tib_chart";
import {setHRVChartOption} from "@/plugins/chart/hrv_chart";
import {setRRChartOption} from "@/plugins/chart/rr_chart";
import {setHRChartOption} from "@/plugins/chart/hr_chart";
import {setAHIChartOption} from "@/plugins/chart/ahi_month_chart";
import { emitter } from '@/plugins/eventBus'
import { getOAHIDevices, isOAHIDevice as checkIsOAHIDevice } from '@/plugins/deviceConfig.js'
import DeviceWaveform from '@/components/DeviceWaveform.vue'

const isLoading = ref(true)

// Tab 切换状态
const activeTab = ref('report')

const props = defineProps({
  device: String,
  selections: Object,
  cohortOrg: String,
  cohortSubjectId: String,
  cohortSite: String,
  date: [String, Date],
  stage: Number,
  /** 数据缺失最小展示阈值（秒），默认 600（10 分钟）；科研场景传 0 展示全部 */
  minMissingSeconds: { type: Number, default: undefined },
})

const keyMap = {
  reporttime: "报告开始计算时间",
  costtime: "报告生成总耗时",
  startc: "数据开始时间",
  endc: "数据结束时间",
  dur: "数据总时长",
  snore_index: "打鼾指数",
  snore_index_onback: "仰卧打鼾指数",
  snore_index_onside: "侧卧打鼾指",
  posion_onback_time: "仰卧时长",
  posion_onback_rate: "仰卧占比",
  posion_onside_time: "侧卧时长",
  posion_onside_rate: "侧卧占比",

  sigtype_0: "无信号",
  sigtype_1: "低信号",
  sigtype_2: "正常信号",
  sigtype_3: "微体动信号",
  sigtype_4: "强体动信号",
  onbedlevel: "在床阈值",
  err_log: "错误信息",
  avg_temp: "平均温度",
  avg_humi: "平均湿度",
  avg_illu: "睡眠时平均光照",
  high_illu: "强光照睡眠",
  sleep_time: "有效睡眠总时间",
  ahi_count: "呼吸事件总数",
  ahi: "综合AHI",
  long_artifact: "体动次数",
  short_artifact: "微体动次数",
  sri: "SRI",
  quality: "睡眠质量分",
  qualitylogs: "睡眠质量细则",
  sleep_dur_0: "睡眠分��_体动和无效时长",
  ahi_count_0: "体动呼吸事件次数（无效）",
  sleep_dur_1: "睡眠分期_Wake时长",
  ahi_count_1: "Wake呼吸事件次数（无效）",
  sleep_dur_2: "睡眠分期_REM时长",
  ahi_count_2: "REM期呼吸事件次数",
  ahi_2: "REM期呼吸指数",
  sleep_dur_3: "睡眠分期_N1时长",
  sleep_dur_4: "睡眠分期_N2时长",
  sleep_dur_5: "睡眠分期_N3时长",
  ahi_count_345: "NREM期呼吸事件次数",
  ahi_345: "NREM期呼吸指数",
  odi_count: "ODI次数",
  odi: "ODI指数",
  min_spo2: "最低血饱和度",
  mean_spo2: "平均血氧饱和度",
  hrv_rmssd_mean: "hrv_rmssd",
  hrv_morning: "hrv_morning",
  hrv_evening: "hrv_evening",
  hrv_delta: "hrv_delta",
  hrv_ratio_mean: "hrv_ratio",
  sleep_range: "睡眠区间调试信息",
  push_message: "push消息",
};
const selectedDevice = ref(props.device)
const selectedReportDate = ref(props.date); // 选中的告日期

// 格式化日期供 DeviceWaveform 使用
const formattedDateForWaveform = computed(() => {
  if (!selectedReportDate.value) return null;
  let dateObj;
  if (typeof selectedReportDate.value === 'string') {
    // 如果是 "YYYY-MM-DD HH:mm:ss" 格式，只取日期部分
    if (selectedReportDate.value.includes(' ')) {
      dateObj = new Date(selectedReportDate.value.split(' ')[0]);
    } else {
      dateObj = new Date(selectedReportDate.value);
    }
  } else if (selectedReportDate.value instanceof Date) {
    dateObj = selectedReportDate.value;
  } else {
    return null;
  }

  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date for waveform:', selectedReportDate.value);
    return null;
  }
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
});

// Read-only organization embed: identity and credentials never enter the iframe URL.
const TARO_ORIGIN = 'https://app.wuji.com'
const taroDialogVisible = ref(false)
const taroIframe = ref(null)
const mobileReportSessionMissing = ref(false)
const mobileReportContext = computed(() => {
  const device = String(selectedDevice.value || '')
  let date = ''
  try { if (selectedReportDate.value) date = reportCalendarDate(selectedReportDate.value) } catch { /* No valid report selected. */ }
  return { device, date, key: `${device}:${date}` }
})
const taroIframeUrl = computed(() => {
  const { device, date } = mobileReportContext.value
  if (!/^[A-Za-z0-9_-]+$/.test(device) || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return ''
  const calendar = new Date(`${date}T00:00:00Z`)
  if (!Number.isFinite(calendar.getTime()) || calendar.toISOString().slice(0, 10) !== date) return ''
  const params = new URLSearchParams({ orgEmbed: '1', device, date,
    lang: currentLocale.value === 'en' ? 'en' : 'zh' })
  return `${TARO_ORIGIN}/?${params}#/pages/home/index`
})
function existingOrgToken() {
  // Read only the existing org session. Do not persist or cache another credential.
  try {
    for (const key of ['authToken_org', 'authToken1_org']) {
      const token = (localStorage.getItem(key) || '').replace(/^\s*Bearer\s+/i, '').trim()
      if (token && !/^Bearer$/i.test(token)) return token
    }
  } catch { /* Unavailable storage is a missing session, not a child timeout. */ }
  return ''
}
function onMobileReportReady(event) {
  if (isResearchRestricted.value) return
  const iframe = taroIframe.value
  const { device, date } = mobileReportContext.value
  if (!taroDialogVisible.value || !iframe || event.origin !== TARO_ORIGIN ||
      event.source !== iframe.contentWindow || iframe.getAttribute('src') !== taroIframeUrl.value) return
  const message = event.data
  if (!message || message.type !== 'research-report-ready' || message.device !== device || message.date !== date) return
  const token = existingOrgToken()
  mobileReportSessionMissing.value = !token
  if (!token) return
  iframe.contentWindow.postMessage({ type: 'research-report-auth', token, device, date }, TARO_ORIGIN)
}
function removeMobileReportListener() {
  window.removeEventListener('message', onMobileReportReady)
}
// Synchronous registration precedes the dialog/iframe mount. Context keys replace old frames;
// the src check also rejects their messages during the render gap after a date/device change.
watch([taroDialogVisible, () => mobileReportContext.value.key], ([visible]) => {
  removeMobileReportListener()
  mobileReportSessionMissing.value = visible && !existingOrgToken()
  if (visible) window.addEventListener('message', onMobileReportReady)
}, { flush: 'sync' })
onBeforeUnmount(removeMobileReportListener)


const reports = ref({}); // 告列表
const reportDates = ref([]); // 报告日期列表
const selections = ref(props.selections)
const userinfo = ref(selections.value['userinfo'])

const deviceinfo = ref(selections.value['deviceinfo'])
const summmary = ref({})
const endData = ref({})
const data = ref({})
const reportTab = ref('night')
const hasNapData = computed(() => {
  const naps = data.value?.nap_detail
  return Array.isArray(naps) && naps.length > 0
})

watch(data, () => {
  reportTab.value = 'night'
})

const chartId = ref(`stage_chart_${Math.random().toString(36).substr(2, 9)}`);
const ArtChartId = ref(`art_chart_${Math.random().toString(36).substr(2, 9)}`);
const EnvChartId = ref(`env_chart_${Math.random().toString(36).substr(2, 9)}`);
const HrvChartId = ref(`hrv_chart_${Math.random().toString(36).substr(2, 9)}`);
const RrChartId = ref(`rr_chart_${Math.random().toString(36).substr(2, 9)}`);
const AhChartId = ref(`ah_chart_${Math.random().toString(36).substr(2, 9)}`);
const HrChartId = ref(`hr_chart_${Math.random().toString(36).substr(2, 9)}`);
const rrBLChartId = ref(`rr_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
const hrBLChartId = ref(`hr_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
const hrvBLChartId = ref(`hrv_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
const ahiChartId = ref(`ahi_chart_${Math.random().toString(36).substr(2, 9)}`);
const spo2ChartId = ref(`spo2_chart_${Math.random().toString(36).substr(2, 9)}`);
const SnoreChartId = ref(`snore_chart_${Math.random().toString(36).substr(2, 9)}`);
const TstChartId = ref(`tst_chart_${Math.random().toString(36).substr(2, 9)}`);
const StageCompChartId = ref(`stage_comp_chart_${Math.random().toString(36).substr(2, 9)}`);
const SnoreIndexChartId = ref(`snore_index_chart_${Math.random().toString(36).substr(2, 9)}`);
const solChartId = ref(`sol_chart_${Math.random().toString(36).substr(2, 9)}`);
const wasoChartId = ref(`waso_chart_${Math.random().toString(36).substr(2, 9)}`);
const seChartId = ref(`se_chart_${Math.random().toString(36).substr(2, 9)}`);
const envChartId = ref(`env_month_chart_${Math.random().toString(36).substr(2, 9)}`);
const deepChartId = ref(`deep_chart_${Math.random().toString(36).substr(2, 9)}`);
const sriChartId = ref(`sri_chart_${Math.random().toString(36).substr(2, 9)}`);
const sleepAgeChartId = ref(`sleep_age_chart_${Math.random().toString(36).slice(2, 11)}`)
const agingPaceChartId = ref(`aging_pace_chart_${Math.random().toString(36).slice(2, 11)}`)
const recoveryChartId = ref(`recovery_chart_${Math.random().toString(36).slice(2, 11)}`)
const reports_data = ref([])
const selectedDate = ref(null)

// 添加新的响应式变量
const essScore = ref(null)
const psqiScore = ref(null)

// 添加获取问卷分数的方法
const fetchQuestionnaireScores = async () => {
  try {
    if (!props.device || !selectedReportDate.value) return
    
    const formattedDate = formatDate(selectedReportDate.value)
    const axios = createAxiosInstance('计算服务器1')
    
    // 获取 ESS 分数
    try {
      const essResponse = await axios.get(`/questionnaire/${props.device}/${formattedDate}/ess`)
      if (essResponse.data.status === 'success' && essResponse.data.data) {
        essScore.value = essResponse.data.data.total_score
      } else {
        essScore.value = null
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('获取 ESS 分数失败:', error)
      }
      essScore.value = null
    }
    
    // 获取 PSQI 分数
    try {
      const psqiResponse = await axios.get(`/questionnaire/${props.device}/${formattedDate}/psqi`)
      if (psqiResponse.data.status === 'success' && psqiResponse.data.data) {
        psqiScore.value = psqiResponse.data.data.total_score
      } else {
        psqiScore.value = null
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('获取 PSQI 分数失败:', error)
      }
      psqiScore.value = null
    }
  } catch (error) {
    console.error('获取问卷分数失败:', error)
    // 确保在出错时也重置分数
    essScore.value = null
    psqiScore.value = null
  }
}

// 添加日期格式化函数
const formatDate = (dateString) => {
  if (!dateString) return ''
  // 如果包含 %20，说明是完整的时间戳，需要截取日期部分
  if (dateString.includes('%20')) {
    return dateString.split('%20')[0]
  }
  // 如果已经是 YYYY-MM-DD 格式，直接返回
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString
  }
  // 其他情况，转换为 YYYY-MM-DD 格式
  const date = new Date(dateString)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const clinicalSite = computed(() => props.cohortSite ?? (selectedCohortState.value?.id === props.cohortOrg ? selectedCohortState.value?.site : '') ?? '')
const cohortContext = computed(() => !!props.cohortOrg)
const profileContext = () => {
  const date = selectedReportDate.value
  const asOf = date instanceof Date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : String(date || '').slice(0, 10)
  return { org: props.cohortOrg, subjectId: props.cohortSubjectId, asOf: props.cohortOrg ? asOf : undefined }
}
const { profile: cohortProfile, loading: profileLoading, saving: profileSaving, error: profileError, refresh: refreshProfile, save: saveProfile } = useSubjectBasicInfo(profileContext)
const showBasicInfoEditor = ref(false)
watch(() => JSON.stringify(profileContext()), () => { showBasicInfoEditor.value = false })
async function saveBasicInfo(patch) {
  if (await saveProfile(patch)) showBasicInfoEditor.value = false
}

// 在获取报告数据时同时获取问卷分数
watch([() => selectedReportDate.value, () => props.device], async ([newDate, newDevice]) => {
  if (newDate && newDevice) {
    await fetchQuestionnaireScores()
  }
})

// 在组件挂载时获取问卷分数
onMounted(async () => {
  if (props.device && selectedReportDate.value) {
    await fetchQuestionnaireScores()
  }
})

// 添加日期格式化函数
const formatChineseDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (currentLocale.value === 'en') return `${year}-${month}-${day}`;
  return `${year}年 ${month}月 ${day}日`;
}

// 日期点击处理
const onDayClick = (day) => {
  // 首先检查是否有报告
  if (!hasReport(day.date)) {
    return; // 如果没有报告，直接返回，不做任何处理
  }
  
  // 格式化日期为 YYYY-MM-DD
  const date = new Date(day.date);
  const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  
  selectedReportDate.value = dateString;
}

// 修改日历组件的配置
const modelConfig = {
  type: 'string',
  mask: 'YYYY-MM-DD', // 日期格式
  disabled: (date) => !hasReport(date) // 禁用没有报告的日期
}

// 判断是否有报告的函数
const hasReport = (date) => {
  const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return reportDates.value.includes(dateString);
}

// 计算最早和最晚的报告日期
const minDate = computed(() => {
  if (reportDates.value.length === 0) return null
  return new Date(Math.min(...reportDates.value.map(d => new Date(d))))
})

const maxDate = computed(() => {
  if (reportDates.value.length === 0) return null
  return new Date(Math.max(...reportDates.value.map(d => new Date(d))))
})

// 修改用日期的定义
const disabledDates = computed(() => {
  // 生成一个日期范围内的所有日期
  const start = minDate.value
  const end = maxDate.value
  const allDates = []

  if (start && end) {
    const current = new Date(start)
    while (current <= end) {
      const dateString = current.toISOString().split('T')[0]
      if (!reportDates.value.includes(dateString)) {
        allDates.push(new Date(current))
      }
      current.setDate(current.getDate() + 1)
    }
  }

  return allDates
})

// 日历标记
const attributes = computed(() => [
  {
    dates: reportDates.value.map(date => new Date(date)),
    dot: {
      color: 'green',
      class: 'report-dot'
    },
    popover: {
      label: t('has_report')
    }
  }
])

// 从 final 数据中提取 summary 相关信息
function extractSummaryFromFinal(finalData) {
  if (!finalData) return {}
  
  // 精简报告接口提供顶层 bpm_min；旧完整报告保留 hr_all.min，兼容两种结构。
  const min_sleep_heart_rate = finalData.bpm_min ?? finalData.hr_all?.min
  
  // 有效数值取整；缺失值保留为 null，避免被误显示成 0。
  const roundNum = (val) => {
    if (val == null || val === '') return null
    const num = parseFloat(val)
    return Number.isFinite(num) ? Math.round(num) : null
  }
  
  return {
    min_sleep_heart_rate: roundNum(min_sleep_heart_rate),
    avg_sleep_heart_rate: roundNum(finalData.bpm_avg),
    avg_hrv_rmssd: roundNum(finalData.hrv_rmssd_mean),
    avg_hrv_radio: roundNum(finalData.hrv_ratio_mean)
  }
}

// 更新数据
function updateEndData() {
  const mappedData = {};
  data.value = reports.value['final']
  // 同步全局显示时区，让所有图表 axisLabel/tooltip 跟随当前报告的时区
  setDisplayTimezone(data.value && data.value.timezone)
  // 分配映射的数据
  if (data.value) {
    Object.keys(keyMap).forEach(key => {
      if (key in data.value) {
        mappedData[keyMap[key]] = data.value[key];
      }
    })
  }
  // 添加'其他'分类
  endData.value = mappedData;
}

//监听日期 选择被试
watch([() => selectedReportDate.value, () => selectedDevice.value], async ([newDate, newDevice], [oldDate, oldDevice]) => {
  if (newDevice == "" || newDevice != oldDevice) {
  } else if (oldDate !== newDate) {
    await fetchReportInfo()
    selectedReportDate.value = newDate
    await getReports()

  }
}, {immediate: true});
//监听日期
watch(() => props.date, async (newVal) => {
  // 当数据变化时，重新渲染图表
  selectedReportDate.value = newVal
  selectedDate.value = selectedReportDate.value

  // 先获取基础信息
  await fetchReportInfo()
  // 再获取报告数据
  await getReports()

}, {deep: true});
watch(() => props.device, async (newVal) => {
  // 当数据变化时，重新渲染图表
  selectedDevice.value = newVal
  await updateDeviceType() // 更新设备类型
  changeDate()
  getReports()
}, {deep: true});
watch(() => props.selections, (newVal) => {
  // 当数据变化时，重新渲染图表
  selections.value = newVal
  userinfo.value = selections.value['userinfo']
  deviceinfo.value = selections.value['deviceinfo']
  getReports()
}, {deep: true});

async function changeDate() {
  reportDates.value = []

  const response = await request_data(`/list_reports/${selectedDevice.value}/`, '计算服务器1');
  // 机构账号数据可见性限制：过滤早于 minVisibleDate 的报告日期（如浙一 2026-07-05）
  const minVisibleDate = getMinVisibleDateForCurrentUser();
  reportDates.value = minVisibleDate
    ? (response.nodes || []).filter(d => d >= minVisibleDate)
    : (response.nodes || []);
  if(selectedReportDate.value){
     return       await getReports()

    }
  if (response.nodes.length > 0 && selectedReportDate.value === '') {
    selectedReportDate.value = response.nodes[0];
    await getReports()

  }
  return response
}

// 防止重复请求的标志
const isLoadingTrend = ref(false);
const lastTrendRequest = ref(null);
let trendRequestGeneration = 0;

async function getReportList(reportdate) {
  let generation;
  try {
    // 确保 reportdate 是有效的日期字符串或时间戳
    if (!reportdate) {
      return;
    }
    
    // ✅ 防止重复请求
    const requestKey = `${selectedDevice.value}-${reportdate}`;
    if (isLoadingTrend.value && lastTrendRequest.value === requestKey) {
      console.log('跳过重复的趋势数据请求');
      return;
    }
    
    generation = ++trendRequestGeneration;
    const isCurrentTrend = () => generation === trendRequestGeneration && lastTrendRequest.value === requestKey && `${selectedDevice.value}-${selectedReportDate.value}` === requestKey;
    isLoadingTrend.value = true;
    lastTrendRequest.value = requestKey;

    reports_data.value = {};
    // 将 reportdate 转换为 Date 对象并验证
    let selectedDate = new Date(reportCalendarDate(reportdate) + 'T12:00:00');
    if (isNaN(selectedDate.getTime())) {
      console.error('Invalid report date:', reportdate);
      return;
    }

    // 计算起始日期和结束日期
    let endDate = new Date(selectedDate);
    let startDate = new Date(selectedDate);
    startDate.setDate(selectedDate.getDate() - 29);

    // 验证期范围
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Invalid date range');
      return;
    }

    // 格式化日期为 yyyy-mm-dd
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let formattedStartDate = formatDate(startDate);
    let formattedEndDate = reportCalendarDate(reportdate);

    // 验证 selectedDevice
    if (!selectedDevice?.value) {
      console.error('Selected device is required');
      return;
    }

    // ✅ 使用优化接口获取趋势数据
    console.log(`准备获取趋势数据: ${selectedDevice.value}, ${formattedStartDate} ~ ${formattedEndDate}`);
    
    try {
      // 尝试使用新的优化接口
      const { getReportTrendData, convertTrendDataToLegacy } = await import('@/plugins/orgDashboardApi.js');
      
      const days = Math.ceil((new Date(formattedEndDate) - new Date(formattedStartDate)) / (1000 * 60 * 60 * 24)) + 1;
      
      console.log(`使用优化接口获取 ${days} 天数据`);
      const trendData = await getReportTrendData(selectedDevice.value, {
        days: days,
        endDate: formattedEndDate,
        anchorDate: formattedEndDate
      });
      
      // 转换为原有格式，兼容现有代码
      // 旧接口返回格式: { "action": "devicereport", "data": { "11/7": {...} } }
      // 所以这里直接使用转换后的对象
      const convertedData = convertTrendDataToLegacy(trendData);
      
      console.log('=== 优化接口返回数据 ===');
      console.log('数据对象:', convertedData);
      console.log('数据键数量:', Object.keys(convertedData).length);
      if (isCurrentTrend()) {
        reports_data.value = convertedData;
      }
      
    } catch (error) {
      console.warn('个人趋势加载失败:', error);
      if (isCurrentTrend()) reports_data.value = {};
    }

  } catch (error) {
    console.error('Error in getReportList:', error);
    // 这里可以添加错误处理逻辑，比如显示错误消息给用户
  } finally {
    // ✅ 请求完成，重置标志
    if (generation === trendRequestGeneration && lastTrendRequest.value === `${selectedDevice.value}-${reportdate}`) isLoadingTrend.value = false;
  }
}


// 修改获取数据和初始化图表的方法
let detailRequestGeneration = 0;
async function getReports() {
  const generation = ++detailRequestGeneration;
  const device = selectedDevice.value;
  const date = selectedReportDate.value;
  const isCurrent = () => generation === detailRequestGeneration && selectedDevice.value === device && selectedReportDate.value === date;
  reports_data.value = {};
  reports.value = {};
  summmary.value = {};
  try {
    isLoading.value = true
    if (!props.device) return
    
    // 处理日期格式
    let reportdate;
    if (selectedReportDate.value) {
      // 如果日期包含时间，只取日期部分
      if (typeof selectedReportDate.value === 'string' && selectedReportDate.value.includes(' ')) {
        reportdate = selectedReportDate.value.split(' ')[0];
      } else {
        reportdate = selectedReportDate.value;
      }
    } else {
      reportdate = "_";
    }
    
    const response = await request_data(`/devicereport/${device}/${reportdate}/`, "计算服务器1");
    if (!isCurrent()) return;
    reports.value = response;
    await getReportList(date)
    if (!isCurrent()) return;
    console.log(reports.value)
    if (reports.value['final']) {
      // 从 final 数据中提取 summary 相关信息
      summmary.value = extractSummaryFromFinal(reports.value['final'])
      updateEndData()
      // 确保 DOM 已更新后再初始化图表
      await nextTick()
      if (!isCurrent()) return;
      await updateCharts()
    }
  } catch (error) {
    console.error('Error fetching reports:', error)
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}
// 监听 selectedReportDate 变化
watch(() => selectedReportDate.value, (newVal) => {
  if (newVal) {
    selectedDate.value = new Date(newVal)
  }
}, { immediate: true })


onMounted(() => {
  changeDate()
});
const formatTime = (timestamp, tz) => {
  if (!timestamp) return '--:--';

  try {
    // 后端 timezone 字段：北京=0, 美西=15。换算到 UTC 偏移：utc_offset = 8 - timezone
    const tzVal = (tz != null) ? tz : (data.value && data.value.timezone) || 0;
    const offsetH = 8 - (Number(tzVal) || 0);
    const date = new Date(timestamp * 1000 + offsetH * 3600 * 1000);

    if (isNaN(date.getTime())) {
      return '--:--';
    }

    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  } catch (error) {
    console.error('时间格式化错误:', error);
    return '--:--';
  }
}

function minutes2HHmm(min) {
  const minutes = Number(min)
  if (!Number.isFinite(minutes)) return '--'
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return h > 0 ? `${h}h${m}min` : `${m}min`
}

const toNapNumber = (value) => {
  if (value == null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const formatNapNumber = (value) => {
  const number = toNapNumber(value)
  if (number == null) return null
  return Number.isInteger(number) ? `${number}` : number.toFixed(1)
}

const formatNapRange = (index) => {
  const range = data.value?.nap_range?.[index]
  const reportStart = toNapNumber(data.value?.start ?? data.value?.starttime)
  const startOffset = toNapNumber(range?.s)
  const endOffset = toNapNumber(range?.e)

  if (reportStart == null || startOffset == null || endOffset == null) {
    return '--:-- - --:--'
  }

  return `${formatTime(reportStart + startOffset)} - ${formatTime(reportStart + endOffset)}`
}

const napReports = computed(() => {
  const naps = Array.isArray(data.value?.nap_detail) ? data.value.nap_detail : []
  const stageDefinitions = [
    { key: 'wake', label: '清醒', field: 'wake_time', color: '#FE8973' },
    { key: 'rem', label: 'REM', field: 'rem_time', color: '#59AAE0' },
    { key: 'light', label: '浅睡', field: 'core_time', color: '#3478F6' },
    { key: 'deep', label: '深睡', field: 'deep_time', color: '#36349D' }
  ]

  return naps.map((nap, index) => {
    const stageValues = stageDefinitions.map((stageItem) => ({
      ...stageItem,
      value: Math.max(0, toNapNumber(nap?.[stageItem.field]) ?? 0)
    }))
    const totalStageTime = stageValues.reduce((total, stageItem) => total + stageItem.value, 0)
    const stages = stageValues.map((stageItem) => ({
      ...stageItem,
      percentage: totalStageTime > 0 ? (stageItem.value / totalStageTime) * 100 : 0
    }))

    const hr = data.value?.nap_hr?.[index]
    const hrMin = formatNapNumber(hr?.min)
    const hrAvg = formatNapNumber(hr?.avg)
    const hrvAvg = formatNapNumber(data.value?.nap_hrv?.[index]?.avg)
    const respAvg = formatNapNumber(data.value?.nap_resp?.[index]?.avg)

    return {
      index,
      timeRange: formatNapRange(index),
      sleepDuration: minutes2HHmm(nap?.sleep_time),
      stages,
      hr: hrMin != null || hrAvg != null ? `${hrMin ?? '--'} / ${hrAvg ?? '--'} bpm` : null,
      hrv: hrvAvg != null ? `${hrvAvg} ms` : null,
      resp: respAvg != null ? `${respAvg} bpm` : null
    }
  })
})

function formatMinutes(minutes) {
  if (Number.isFinite(minutes)) {  // 检查minutes是否为一个有限的数
    const roundedMinutes = Math.round(minutes);
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    if (hours === 0) {
      return `${remainingMinutes}${t('minutes_suffix')}`;
    }

    return `${hours}${t('hour_char')} ${remainingMinutes}${t('minute_char')}`;
  }
  return '';
}

const getSleepEfficiency = (tst, tib) => {
  if (!tst || !tib) return '--'
  return ((tst / tib) * 100).toFixed(0)
}
// 计算属性
const computedSleepAnalysis = computed(() => {
  if (!data.value) return []

  return [
    // 时间相关
    {label: t('lights_off_time'), value: formatTime(data.value.sleep_intention_time)},
    {label: t('lights_on_time'), value: formatTime(data.value.intention_leave_time)},
    {label: t('total_sleep_time_tst'), value: formatMinutes(data.value.tst)},
    {label: t('sleep_efficiency_se'), value: `${getSleepEfficiency(data.value.tst, data.value.tib)}%`},
    
    // 睡眠质量相关
    {label: t('sleep_onset_sol'), value: formatMinutes(data.value.sol)},
    {label: t('sleep_interruption_waso'), value: formatMinutes(data.value.waso)+' ('+data.value.wis+t('times_suffix')+')'},
    {label: t('deep_sleep_t'), value: formatMinutes(data.value.deep_time)},
    {label: t('rem_latency_label'), value: formatMinutes(data.value.rem_latency)},
    
    // 睡眠评分相关
    {label: t('sleep_regularity_sri'), value: (data.value.sri != null && data.value.sri >= 0) ? `${data.value.sri}%` : '--'},
    {label: t('sleep_fragmentation_sfi'), value: data.value.sfi?.toFixed(1) || '--'}
  ]
})

const computedSleepPhases = computed(() => {
  if (!data.value) return []

  const totalTime = data.value.tst || 0
  return [
    {
      label: t('awake'),
      value: formatMinutes(data.value.sleep_dur_1),
      percentage: data.value.sleep_dur_1_efficiency ? `${((data.value.sleep_dur_1 / totalTime) * 100).toFixed(0)}%` : '--'
    },
    {
      label: t('rem_short'),
      value: formatMinutes(data.value.rem_time),
      percentage: data.value.rem_efficiency ? `${(data.value.rem_efficiency * 100).toFixed(0)}%` : '--'
    },
    {
      label: t('light_sleep_short'),
      value: formatMinutes(data.value.core_time),
      percentage: data.value.core_efficiency ? `${(data.value.core_efficiency * 100).toFixed(0)}%` : '--'
    },
    {
      label: t('deep_sleep_short'),
      value: formatMinutes(data.value.deep_time),
      percentage: data.value.deep_efficiency ? `${(data.value.deep_efficiency * 100).toFixed(0)}%` : '--'
    }
  ]
})

// 添加设备号判断的响应式变量
const isOAHIDevice = ref(false)

// 检查设备是否为 OAHI 设备
const updateDeviceType = async () => {
  if (selectedDevice.value) {
    console.log('OrgReportDetail - 检查设备类型:', selectedDevice.value)
    isOAHIDevice.value = await checkIsOAHIDevice(selectedDevice.value)
    console.log('OrgReportDetail - 设备', selectedDevice.value, '是否为OAHI:', isOAHIDevice.value)
  }
}

// 预处理 OAHI 数据，将 oahi 字段映射为 ahi 字段供图表使用
const preprocessOAHIData = (data) => {
  if (!data || !Array.isArray(data)) return data
  
  return data.map(item => {
    if (item && typeof item === 'object') {
      return {
        ...item,
        ahi: item.oahi || item.OAHI || item.ahi,
        ahi_2: item.oahi_2 || item.ahi_2,
        ahi_345: item.oahi_345 || item.ahi_345
      }
    }
    return item
  })
}

const computedBreathingHealth = computed(() => {
  if (!data.value) return []

  // 根据设备号选择使用 AHI 还是 OAHI 数据
  if (isOAHIDevice.value) {
    return [
      {label: '阻塞性呼吸暂停低通气指数,OAHI', value: (data.value.oahi || data.value.OAHI)?.toFixed(1) || '--'},
      {label: 'NREM OAHI', value: data.value.oahi_345?.toFixed(1) || '--'},
      {label: 'REM OAHI', value: data.value.oahi_2?.toFixed(1) || '--'},
      {label: t('snore_index_si'), value: data.value.bcgsnore_index?.toFixed(1) || '--'},
      {
        label: t('sleep_avg_rr'),
        value: data.value.avg_resp ? `${data.value.avg_resp.toFixed(1)}${t("bpm_unit")}` : '--'
      }
    ]
  } else {
    return [
      {label: t('ahi_full_label_comma'), value: data.value.ahi?.toFixed(1) || '--'},
      {label: 'NREM AHI', value: data.value.ahi_345?.toFixed(1) || '--'},
      {label: 'REM AHI', value: data.value.ahi_2?.toFixed(1) || '--'},
      {label: t('snore_index_si'), value: data.value.bcgsnore_index?.toFixed(1) || '--'},
      {
        label: t('sleep_avg_rr'),
        value: data.value.avg_resp ? `${data.value.avg_resp.toFixed(1)}${t("bpm_unit")}` : '--'
      }
    ]
  }
})


// 格式化数字为整数的函数
const formatInteger = (val) => {
  if (val == null || val === '' || val === undefined) return null
  const num = parseFloat(val)
  if (isNaN(num)) return null
  return Math.round(num).toString()
}

const computedSleepPeriodIndicators = computed(() => {
  if (!data.value) return []

  const minHeartRate = formatInteger(summmary.value.min_sleep_heart_rate)
  const avgHeartRate = formatInteger(summmary.value.avg_sleep_heart_rate)
  // HRV RMSSD：优先从 final/online 的 hrv_rmssd_mean 获取，其次从 summary 的 avg_hrv_rmssd 获取，保留两位小数
  const hrvRmssd = (() => {
    const value = data.value.hrv_rmssd_mean != null 
      ? parseFloat(data.value.hrv_rmssd_mean) 
      : (summmary.value.avg_hrv_rmssd != null 
          ? parseFloat(summmary.value.avg_hrv_rmssd) 
          : null);
    return value != null ? value.toFixed(2) : null;
  })()
  // HRV LF/HF 直接从 hrv_ratio_mean 获取，保留两位小数
  const hrvRadio = data.value.hrv_ratio_mean != null ? parseFloat(data.value.hrv_ratio_mean).toFixed(2) : null

  return [
    {
      label: t('sleep_min_hr'),
      value: minHeartRate !== null ? `${minHeartRate}${t('bpm_unit')}` : '--'
    },
    {
      label: t('sleep_avg_hr'),
      value: avgHeartRate !== null ? `${avgHeartRate}${t('bpm_unit')}` : '--'
    },
    {
      label: t('sleep_hrv_rmssd'),
      value: hrvRmssd !== null ? `${hrvRmssd}${t('ms_unit')}` : '--'
    },
    {
      label: t('sleep_hrv_lfhf'),
      value: hrvRadio !== null ? hrvRadio : '--'
    }
  ]
})
const computedSleepEnv = computed(() => {
  if (!data.value) return []
  // 环境字段（温度/湿度/光照影响比例）后端可能返回 null / undefined / 非数字，
  // parseFloat 后是 NaN，直接模板拼接会渲染成 "NaN°C" / "NaN%"，统一兜底成 "--"
  const fmt = (val, unit) => {
    const num = parseFloat(val)
    return Number.isFinite(num) ? `${Math.round(num)}${unit}` : '--'
  }
  const fmtRatio = (val) => {
    const num = parseFloat(val)
    return Number.isFinite(num) ? `${Math.round(num * 100)}%` : '--'
  }
  return [
    { label: t('avg_temperature'), value: fmt(data.value.avg_temp, '°C') },
    { label: t('avg_humidity'), value: fmt(data.value.avg_humi, '%') },
    { label: t('light_affect_ratio'), value: fmtRatio(data.value.high_illu) },
  ]
})
// 处理窗口大小变化
const handleResize = () => {
  Object.entries(chartInstances.value).forEach(([key, chart]) => {
    try { chart?.resize() }
    catch (error) { console.warn(`图表 ${key} resize 失败:`, error) }
  })
}

// Every chart belongs to this report component, including monthly trends.
const destroyCharts = () => {
  Object.values(chartInstances.value).forEach(chart => chart?.dispose())
  chartInstances.value = {}
}
const chartRefs = ref({})
const chartInstances = ref({})
let chartUpdateTimer = null
let chartUnmounted = false
watch([selectedDevice, selectedReportDate], () => {
  trendRequestGeneration++
  lastTrendRequest.value = null
  isLoadingTrend.value = false
  reports_data.value = {}
  for (const key of ['sleepAge', 'agingPace', 'recovery']) chartInstances.value[key]?.clear()
}, { flush: 'sync' })
watch([activeTab, reportTab], async () => {
  await nextTick()
  if (!chartUnmounted) handleResize()
})
// 初始化所有图表
const initCharts = () => {
  const chartIds = {
    stage: chartId.value,
    art: ArtChartId.value,
    hr: HrChartId.value,
    hrv: HrvChartId.value,
    rr: RrChartId.value,
    spo2: spo2ChartId.value,
    ah: AhChartId.value,
    snoreIndex: SnoreIndexChartId.value,
    env: envChartId.value,
    env_day: EnvChartId.value,
    tst: TstChartId.value,
    stageComp: StageCompChartId.value,
    deep: deepChartId.value,
    se: seChartId.value,
    sol: solChartId.value,
    waso: wasoChartId.value,
    snore: SnoreChartId.value,
    sri: sriChartId.value,
    ahi: ahiChartId.value,
    rrbl: rrBLChartId.value,
    hrbl: hrBLChartId.value,
    hrvbl: hrvBLChartId.value,
    sleepAge: sleepAgeChartId.value,
    agingPace: agingPaceChartId.value,
    recovery: recoveryChartId.value,
  }

  // 确保先销毁现有实例
  destroyCharts()

  // 重新初始化图表
  Object.entries(chartIds).forEach(([key, id]) => {
    const el = document.getElementById(id)
    if (el) {
      chartInstances.value[key] = echarts.init(el)
    }
  })
}


// 所有图表
const updateCharts = async () => {
  if (chartUnmounted || !Object.keys(data.value || {}).length) return
  clearTimeout(chartUpdateTimer)
  // 确保 DOM 已更新
  await nextTick()
  if (chartUnmounted) return
  // 如果图表实例不存在，重新初始化
  if (Object.keys(chartInstances.value).length === 0) {
    initCharts()
  }
  // 先初始化图表日期（使用 M/D 格式）
  initChartDates(reports_data.value, selectedReportDate.value)

  // parseReportsToDisplay 处理数组格式，但我们现在的数据是对象格式
  // 如果 reports_data.value 是对象，跳过 parseReportsToDisplay
  // 因为对象格式的数据已经通过 initChartDates 处理了
  if (Array.isArray(reports_data.value)) {
  parseReportsToDisplay(reports_data.value)
  } else {
    console.log('reports_data.value 是对象格式，跳过 parseReportsToDisplay')
  }
  console.log('=== 图表更新前数据检查 ===')
console.log('reports_data.value:', reports_data.value)
  console.log('reports_data.value 类型:', typeof reports_data.value)
  console.log('reports_data.value 是否为对象:', reports_data.value && typeof reports_data.value === 'object')
  console.log('reports_data.value 的键:', reports_data.value ? Object.keys(reports_data.value) : 'N/A')
console.log('chart_dates:', chart_dates)
  console.log('chart_dates 的键:', Object.keys(chart_dates))
  
  // 确保 reports_data.value 是正确的格式
  if (reports_data.value && typeof reports_data.value === 'object' && !Array.isArray(reports_data.value)) {
    // 检查是否有 reports 字段（说明可能是原始响应对象）
    if ('reports' in reports_data.value && Array.isArray(reports_data.value.reports)) {
      console.warn('⚠️ reports_data.value 仍然是原始响应对象，需要转换！')
      // 重新转换数据
      try {
        const { convertTrendDataToLegacy } = await import('@/plugins/orgDashboardApi.js');
        const convertedData = convertTrendDataToLegacy(reports_data.value);
        reports_data.value = convertedData;
        console.log('✅ 已重新转换数据，新键:', Object.keys(reports_data.value));
      } catch (error) {
        console.error('❌ 转换数据失败:', error);
      }
    }
  }
  const baseData = {
    start: data.value['starttime'],
    end: data.value['endtime'],
    stages: data.value['stage_details'],
    nointention: (data.value['intention_middle'] && data.value['intention_middle'].length > 0) ? data.value['intention_middle'] : null,
    leavebed: (data.value['leavebed'] && data.value['leavebed'].length > 0) ? data.value['leavebed'] : null,
    signalMissingIntervals: data.value['signal_missing_intervals'] || [],
    minMissingSeconds: props.minMissingSeconds,
  }
  clearTimeout(chartUpdateTimer)
  chartUpdateTimer = setTimeout(() => {
    if (chartUnmounted) return
    const healthPoints = buildHealthTrendDays(reports_data.value, reportCalendarDate(selectedReportDate.value))
    const healthLabels = {
      age: t('research_trend_sleep_age'), pace: t('research_trend_aging_speed'),
      avg28d: t('research_trend_28d'), recovery: t('research_trend_recovery'),
      years: t('research_trend_years'), yearsPerYear: t('research_trend_years_per_year'),
    }
    // 更新每个图表
    Object.entries(chartInstances.value).forEach(([key, chart]) => {
      if (!chart) return

      try {
        switch (key) {
          case 'sleepAge':
            chart.setOption(buildSleepAgeTrendOption(healthPoints, healthLabels), { notMerge: true })
            break
          case 'agingPace':
            chart.setOption(buildAgingPaceTrendOption(healthPoints, healthLabels), { notMerge: true })
            break
          case 'recovery':
            chart.setOption(buildRecoveryScoreTrendOption(healthPoints, healthLabels), { notMerge: true })
            break
          case 'stage':
            setStageChartOption(chart, {...baseData})
            break
          case 'art':
            // 根据数据类型选择不同的渲染方式
            if (data.value['artifact_series']) {
              setArt2ChartOption(chart, {
                ...baseData,
                segments: data.value['artifact_series']
              })
            } else {
              setArtChartOption(chart, {
                ...baseData,
                segments: data.value['artifact_series']
              })
            }
            break
          case 'hr':
            setDayHRChartOption(chart, {
              ...baseData,
              segments: data.value.bpm_series,
              avg: data.value.bpm_avg
            })
            break
          case 'hrv':
            setDayHRVChartOption(chart, {
              ...baseData,
              segments: data.value.hrv_rmssd,
              avg: data.value.hrv_rmssd_mean
            })
            break
          case 'rr':
            setDayRRChartOption(chart, {
              ...baseData,
              segments: data.value.resp_series,
              avg: data.value.avg_resp
            })
            break
          case 'spo2':
            setSpO2ChartOption(chart, {
              ...baseData,
              spo2: data.value.spo2_data
            })
            break
          case 'ah':
            // 根据设备号选择使用 AHI 还是 OAHI 数据
            if (isOAHIDevice.value) {
              // OAHI 设备使用 oahi 相关数据
              if (data.value.oahi_series) {
                setStageAH2ChartOption(chart, {
                  ...baseData,
                  ah: data.value.oahi_series || data.value.OAHI_event
                })
              } else {
                setStageAHChartOption(chart, {
                  ...baseData,
                  ah: data.value.OAHI_event || data.value.oahi_events
                })
              }
            } else {
              // 普通设备使用 ahi 数据
              if (data.value.ahi_series) {
                setStageAH2ChartOption(chart, {
                  ...baseData,
                  ah: data.value['ahi_series'] || data.value['ahi_events']
                })
              } else {
                setStageAHChartOption(chart, {
                  ...baseData,
                  ah: data.value.ahi_events
                })
              }
            }
            break
          case 'snoreIndex':
          console.log('=== 鼾声指数图表数据 ===')
  console.log('传入的reports_data:', reports_data.value)
  console.log('传入的chart_dates:', chart_dates)
            setSnoreIndexChartOption(reports_data.value, chart, chart_dates);

            break
          case 'env':
            setEnvChartOption(reports_data.value, chart, chart_dates)
            break
          case 'env_day':
            setDayEnvChartOption(chart, {
              ...baseData,
              segments: data.value.thi_series
            })
            break
            // 趋势图表
          case 'tst':
            setTSTChartOption(reports_data.value, chart, chart_dates)
            break
          case 'stageComp':
            setStageCompChartOption(reports_data.value, chart, chart_dates)
            break
          case 'deep':
            setDeepChartOption(reports_data.value, chart, chart_dates)
            break
          case 'se':
            setSEChartOption(reports_data.value, chart, chart_dates)
            break
          case 'sol':
            setSOLChartOption(reports_data.value, chart, chart_dates)
            break
          case 'waso':
            setWASOChartOption(reports_data.value, chart, chart_dates)
            break
          case 'ahi':
            // 为 OAHI 设备预处理数据
            if (isOAHIDevice.value) {
              const processedData = preprocessOAHIData(reports_data.value)
              setAHIChartOption(processedData, chart, chart_dates)
            } else {
              setAHIChartOption(reports_data.value, chart, chart_dates)
            }
            break
          case 'rrbl':
            setRRChartOption(reports_data.value, chart, chart_dates)
            break
          case 'hrbl':
            setHRChartOption(reports_data.value, chart, chart_dates)
            break
          case 'hrvbl':
            setHRVChartOption(reports_data.value, chart, chart_dates)
            break
          case 'snore':
            setDaySnoreChartOption(chart, {
              ...baseData,
              snore: data.value['bcgsnore_events']
            })
            break
          case 'sri':
            setTIBChartOption(reports_data.value, chart, chart_dates);
            break
        }
        if (monthlyTrendKeys.has(key)) alignMonthlyTrendAxis(chart, healthPoints)
      } catch (error) {
        console.error(`更新图表 ${key} 失败:`, error)
      }
      finally{
        isLoading.value = false

      }
    })
  }, 100)
}

const parseReportsToDisplay = (reports) => {
  //console.log("History:parseReportsToDisplay");

  // 传入了报告列表，是长时间趋势图模式
  console.log('=== 开始解析月趋势数据 ===')
  console.log('传入的reports:', reports)
  console.log('reports类型:', typeof reports)
  console.log('reports长度:', Array.isArray(reports) ? reports.length : '不是数组')

  var i;
  if (reports && reports.length > 0) {
    console.log('第一条报告数据:', reports[0])
    console.log('第一条报告字段:', Object.keys(reports[0]))
  
    this.setData({
      reportCount: reports ? reports.length : 0,
    });
    let max_chart_days = 0
    if (this.data.period === 'week') {
      max_chart_days = 7;
    } else if (this.data.period === 'month') {
      max_chart_days = 30;
    } else if (this.data.period === 'year') {
      max_chart_days = 365;
    }

    //生成空的日期序列，用于各表的日期轴
    var today = new Date(reports[0].end * 1000); //TODO：注意：这里使用第一份有效报告作为横轴最新节点，可能会导致跟取数据库的时间区间对不齐
    today.setHours(0, 0, 0, 0);
    var todayTS = today.getTime() / 1000; //按今天零点对齐
    resetDates(); //先清空
    for (i = 0; i < max_chart_days; i++) {
      var shortDate = formatDateString(todayTS * 1000, "yyyyMMdd", false);
      chart_dates[shortDate] = -1;
      today.setDate(today.getDate() - 1);
      todayTS = today.getTime() / 1000;
    }
    //在空日期序列中填充有效报告数据的索引
    todayTS = today.getTime() / 1000; //重新按今天零点对齐
    reports.forEach((value, index, array) => {
      var shortDate = formatDateString(value.end * 1000, "yyyyMMdd", false);
      if (chart_dates[shortDate] === -1) {
        chart_dates[shortDate] = index;
      } else if (chart_dates[shortDate] >= 0) {
        let idx = chart_dates[shortDate];
        if (array[idx].report_time < value.report_time) {
          chart_dates[shortDate] = index;
        }
      }
    });
    //去掉日期序列中开头的空白段
    var keysDate = Object.keys(chart_dates).reverse();
    for (i = 0; i < keysDate.length; i++) {
      if (chart_dates[keysDate[i]] >= 0) {
        break;
      } else if (i < keysDate.length - 29) { // 保留后 14 天，哪怕空白，为了图表不要太短
        delete chart_dates[keysDate[i]];
      }
    }
    //console.log(chart_dates);

  }
}

function initChartDates(reports, reportKey = '') {
  if (!reports) return;

  const max_chart_days = 30;
  let today;

  try {
    if (reportKey) {
      today = new Date(reportCalendarDate(reportKey) + 'T12:00:00');
      if (isNaN(today.getTime())) {
        throw new Error('Invalid date');
      }
    } else {
      today = new Date();
    }

    today.setHours(0, 0, 0, 0);
    let todayTS = today.getTime() / 1000;
    resetDates();

    // 生成日期序列，使用 M/D 格式（与 convertTrendDataToLegacy 返回的格式一致）
    for (let i = 0; i < max_chart_days; i++) {
      const date = new Date(todayTS * 1000);
      const month = date.getMonth() + 1;  // 不补零
      const day = date.getDate();         // 不补零
      const shortDate = `${month}/${day}`;
      chart_dates[shortDate] = -1;
      today.setDate(today.getDate() - 1);
      todayTS = today.getTime() / 1000;
    }

    // 如果 reports 是对象格式（新接口返回的格式），填充报告数据映射
    if (reports && typeof reports === 'object' && !Array.isArray(reports)) {
      Object.entries(reports).forEach(([dateKey, report]) => {
        // dateKey 应该是 "M/D" 格式（如 "11/7"）
        if (chart_dates[dateKey] !== undefined) {
          // 如果 chart_dates 中已有这个键，将其标记为有效
          if (chart_dates[dateKey] === -1) {
            chart_dates[dateKey] = dateKey;  // 存储实际的日期键，用于后续查找
          }
        }
      });
    }
  } catch (error) {
    console.error('Error initializing chart dates:', error);
  }
}

// 生命周期钩子
onMounted(async () => {
  try {
    isLoading.value = true
    await updateDeviceType() // 初始化设备类型
    await changeDate()
    await fetchReportInfo()
    await nextTick()
    window.addEventListener('resize', handleResize)
  } finally {
    // isLoading.value = false
  }
})
// onMounted(() => {
//   initCharts()
//   updateCharts()
//   window.addEventListener('resize', handleResize)
// })

onBeforeUnmount(() => {
  chartUnmounted = true
  clearTimeout(chartUpdateTimer)
  trendRequestGeneration++
  detailRequestGeneration++
  lastTrendRequest.value = null
  window.removeEventListener('resize', handleResize)
  destroyCharts()
  // 复位全局显示时区，避免影响列表页等其他视图
  setDisplayTimezone(0)
})

// 监听数据变化
watch(() => data.value, updateCharts, {deep: true})
watch(() => reports_data.value, updateCharts, {deep: true})

// 添加一个函数来计算进度条宽度
const getProgressWidth = (item) => {
  // 根据不同类型的数据返回不同的进度值
  if (item.label.includes('度')) {
    // 假设适宜温度范围是18-26度
    const temp = parseFloat(item.value)
    return `${Math.min(100, Math.max(0, ((temp - 18) / (26 - 18)) * 100))}%`
  }
  if (item.label.includes('湿度')) {
    // 假设适宜湿度范围是30-70%
    const humidity = parseFloat(item.value)
    return `${Math.min(100, (humidity / 100) * 100)}%`
  }
  if (item.label.includes('光照')) {
    // 直接使用百分比值
    const light = parseFloat(item.value)
    return `${Math.min(100, light)}%`
  }
  return '50%' // 默认值
}

// 计算年龄
// 性别显示映射（后端可能返回 '男'/'女' / 'M'/'F' / '1'/'0'）
const sexDisplay = computed(() => {
  const raw = userinfo.value?.sex;
  if (raw === undefined || raw === null || raw === '') return '--';
  const s = String(raw).trim().toLowerCase();
  if (s === '男' || s === 'm' || s === 'male' || s === '1') return t('gender_male');
  if (s === '女' || s === 'f' || s === 'female' || s === '0' || s === '2') return t('gender_female');
  return raw;
});

const computedAge = computed(() => {
  if (!userinfo.value?.birthdate) return '--';
  // 处理 yyyy-mm 格式的生日
  const birthDateParts = userinfo.value.birthdate.split('-');
  const birthYear = parseInt(birthDateParts[0], 10);
  const birthMonth = parseInt(birthDateParts[1], 10) - 1; // 月份从0开始
  const birthdate = birthDateParts[2] ? parseInt(birthDateParts[2], 10) : 1; // 默认使用1号

  const birthDate = new Date(birthYear, birthMonth, birthdate);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return `${age}${t('years_suffix')}`;
});

// 计算 BMI
const computedBMI = computed(() => {
  if (!userinfo.value?.height || !userinfo.value?.weight) return '--'
  const height = userinfo.value.height / 100 // 转换为米
  const weight = userinfo.value.weight
  const bmi = (weight / (height * height)).toFixed(1)
  return bmi
})

// 基础信息配置
const baseInfoItems = computed(() => cohortContext.value ? basicInfoItems(cohortProfile.value, t) : [
  {
    label: t('name'),
    value: userinfo.value?.username || '--'
  },
  {
    label: t('gender'),
    value: sexDisplay.value
  },
  {
    label: t('birthdate'),
    value: userinfo.value?.birthdate || '--'
  },
  {
    label: t('age'),
    value: computedAge
  },
  {
    label: t('ethnicity'),
    value: userinfo.value?.ethnicity || '--'
  },
  {
    label: t('height'),
    value: userinfo.value?.height ? `${userinfo.value.height} cm` : '--'
  },
  {
    label: t('weight'),
    value: userinfo.value?.weight ? `${userinfo.value.weight} kg` : '--'
  },
  {
    label: 'BMI',
    value: computedBMI
  }
])
// 自定义字段处理
const customFields = computed(() => {
  const customData = cohortContext.value ? {} : userinfo.value?.customData || {}
  
  // 直接返回处理后的对象,保持原有的数据结构
  return Object.entries(customData).reduce((acc, [key, fieldData]) => {
    // 如果数据格式不正确,返回默认值
    if (!fieldData || typeof fieldData !== 'object') {
      acc[key] = {
        value: '--',
        unit: ''
      }
      return acc
    }

    // 保持原有的值和单位
    acc[key] = {
      value: fieldData.value || '--',
      unit: fieldData.unit || ''
    }
    return acc
  }, {})
})

// 从 API 获取用户资料
const fetchUserProfile = async (openid) => {
  if (!openid) {
    console.warn('[fetchUserProfile] openid 为空，跳过获取用户资料');
    return null;
  }
  
  try {
    const apiAxiosInstance = createAxiosInstance('API服务器');
    const response = await apiAxiosInstance.get('/api/user/profile', {
      params: {
        openid: openid
      }
    });
    
    if (response.data && !response.data.error) {
      console.log('[fetchUserProfile] 获取用户资料成功:', response.data);
      return response.data;
    } else {
      console.warn('[fetchUserProfile] 获取用户资料失败:', response.data?.error);
      return null;
    }
  } catch (error) {
    console.error('[fetchUserProfile] 获取用户资料异常:', error);
    return null;
  }
}

// 修改 fetchReportInfo 方法
const fetchReportInfo = async () => {
  // Cohort profiles are fetched by frozen subject identity, not report/device-owner fallbacks.
  if (cohortContext.value) return
  try {
    const axiosInstance = createAxiosInstance('计算服务器1');
    
    // 格式化日期
    let formattedDate;
    if (selectedReportDate.value instanceof Date) {
      formattedDate = selectedReportDate.value.toISOString().split('T')[0];
    } else {
      const date = new Date(selectedReportDate.value);
      formattedDate = date.toISOString().split('T')[0];
    }
    const response = await axiosInstance.get(`report_info/${props.device}/${formattedDate}/`);
    const isMVP3Org = localStorage.getItem('userOrg') === 'MVP3被试摸底'
    // 检查 response.data.data 是否为 null
    if (response.data.status === 'success' && response.data.data) {
      const { basicInfo, customFields: customFieldsData } = response.data.data;
      
      // 更新 userinfo
      userinfo.value = {
        username: basicInfo.name || '',
        sex: basicInfo.gender || '',
        ethnicity: basicInfo.ethnicity || '',
        birthday: formatDateValue(basicInfo.birthDate),
        age: basicInfo.age || '',
        height: basicInfo.height || null,
        weight: basicInfo.weight || null,
        bmi: basicInfo.bmi || '',
        customData: customFieldsData || {}
      };
      
      // 如果有 openid，尝试从 API 获取最新的用户资料
      const openid = selections.value?.userinfo?.openid || userinfo.value?.openid || props.selections?.userinfo?.openid;
      if (openid) {
        console.log('[fetchReportInfo] 检测到 openid，尝试从 API 获取用户资料:', openid);
        const profileData = await fetchUserProfile(openid);
        if (profileData) {
          // 合并 API 返回的数据：
          // - MVP3被试摸底：不覆盖已经有值的字段（避免 /userinfo 补齐后又被旧 profile 覆盖回去）
          // - 其他机构：保持原逻辑（profile 优先）
          userinfo.value = isMVP3Org
            ? {
                ...userinfo.value,
                username: userinfo.value.username || profileData.username || '',
                sex: userinfo.value.sex || profileData.sex || '',
                birthdate: userinfo.value.birthdate || profileData.birthdate || userinfo.value.birthday || '',
                birthday: userinfo.value.birthday || (profileData.birthdate ? formatDateValue(profileData.birthdate) : '') || '',
                height: userinfo.value.height || profileData.height || null,
                weight: userinfo.value.weight || profileData.weight || null,
                diagnosis: userinfo.value.diagnosis || profileData.diagnosis || []
              }
            : {
                ...userinfo.value,
                username: profileData.username || userinfo.value.username || '',
                sex: profileData.sex || userinfo.value.sex || '',
                birthdate: profileData.birthdate || userinfo.value.birthdate || userinfo.value.birthday || '',
                birthday: profileData.birthdate ? formatDateValue(profileData.birthdate) : (userinfo.value.birthday || userinfo.value.birthdate || ''),
                height: profileData.height || userinfo.value.height || null,
                weight: profileData.weight || userinfo.value.weight || null,
                diagnosis: profileData.diagnosis || userinfo.value.diagnosis || []
              };
          console.log('[fetchReportInfo] 已更新 userinfo 为 API 返回的数据');
        }
      }
    } else {
      // 当 response.data.data 为 null 或服务器没有数据时
      useDefaultUserInfo();

      // MVP3被试摸底：即使 report_info 为空，也尝试从 devicereport -> /userinfo 补齐基本信息
      const filledFromDeviceReport = await tryFetchUserInfoFromDeviceReport(formattedDate);
      
      // 即使没有报告信息，也尝试从 API 获取用户资料
      const openid = selections.value?.userinfo?.openid || userinfo.value?.openid || props.selections?.userinfo?.openid;
      if (openid && !(isMVP3Org && filledFromDeviceReport)) {
        console.log('[fetchReportInfo] 没有报告信息，但检测到 openid，尝试从 API 获取用户资料:', openid);
        const profileData = await fetchUserProfile(openid);
        if (profileData) {
          // 合并 API 返回的数据
          userinfo.value = {
            ...userinfo.value,
            username: profileData.username || userinfo.value.username || '',
            sex: profileData.sex || userinfo.value.sex || '',
            birthdate: profileData.birthdate || userinfo.value.birthdate || userinfo.value.birthday || '',
            birthday: profileData.birthdate ? formatDateValue(profileData.birthdate) : (userinfo.value.birthday || userinfo.value.birthdate || ''),
            height: profileData.height || userinfo.value.height || null,
            weight: profileData.weight || userinfo.value.weight || null,
            diagnosis: profileData.diagnosis || userinfo.value.diagnosis || []
          };
          console.log('[fetchReportInfo] 已更新 userinfo 为 API 返回的数据');
        }
      }
    }
  } catch (error) {
    console.error('[fetchReportInfo] 获取报告信息失败:', error);
    useDefaultUserInfo();

    // MVP3被试摸底：report_info 失败时也补一条 fallback
    try {
      let formattedDate;
      if (selectedReportDate.value instanceof Date) {
        formattedDate = selectedReportDate.value.toISOString().split('T')[0];
      } else {
        const date = new Date(selectedReportDate.value);
        formattedDate = date.toISOString().split('T')[0];
      }
      await tryFetchUserInfoFromDeviceReport(formattedDate);
    } catch (_) {}
    
    // 即使出错，也尝试从 API 获取用户资料
    const openid = selections.value?.userinfo?.openid || userinfo.value?.openid || props.selections?.userinfo?.openid;
    if (openid) {
      console.log('[fetchReportInfo] 获取报告信息出错，但检测到 openid，尝试从 API 获取用户资料:', openid);
      const profileData = await fetchUserProfile(openid);
      if (profileData) {
        userinfo.value = {
          ...userinfo.value,
          username: profileData.username || userinfo.value.username || '',
          sex: profileData.sex || userinfo.value.sex || '',
          birthdate: profileData.birthdate || userinfo.value.birthdate || userinfo.value.birthday || '',
          birthday: profileData.birthdate ? formatDateValue(profileData.birthdate) : (userinfo.value.birthday || userinfo.value.birthdate || ''),
          height: profileData.height || userinfo.value.height || null,
          weight: profileData.weight || userinfo.value.weight || null,
          diagnosis: profileData.diagnosis || userinfo.value.diagnosis || []
        };
      }
    }
  }
}

/**
 * MVP3被试摸底：从 /devicereport/{device}/{date}/ 中提取 phone/openid，再通过 fetchUserProfile(phone) 拉取基本信息
 * 目的：设备解绑后 deviceinfo 里没 user 也能在报告详情展示“基本信息”
 */
const tryFetchUserInfoFromDeviceReport = async (formattedDate) => {
  try {
    const userOrg = localStorage.getItem('userOrg')
    if (userOrg !== 'MVP3被试摸底') return false
    if (!props.device || !formattedDate) return false

    // 取报告详情（计算服务器1）
    const report = await request_data(`/devicereport/${props.device}/${formattedDate}/`, '计算服务器1').catch(() => null)
    if (!report || typeof report !== 'object') return false

    const finalData = report.final || report['final'] || {}
    const onlineData = report.online || report['online'] || {}
    const interventions = report.interventions || finalData.interventions || onlineData.interventions || {}

    // 提取 phoneNumber/user
    const phone =
      interventions.phoneNumber || interventions.user ||
      finalData.phoneNumber || finalData.user || finalData.phonenumber || finalData.phonenumnber ||
      onlineData.phoneNumber || onlineData.user || onlineData.phonenumber || onlineData.phonenumnber ||
      null

    if (!phone) return false

    const profile = await fetchUserProfileByPhone(phone).catch(() => null)
    if (!profile || typeof profile !== 'object') return false

    // 新接口返回扁平结构，nickname 对应旧 username
    const normalized = {
      username: profile.nickname || userinfo.value?.username || '',
      sex: profile.sex || userinfo.value?.sex || '',
      ethnicity: profile.ethnicity || userinfo.value?.ethnicity || '',
      birthday: profile.birthdate || userinfo.value?.birthday || '',
      age: profile.age || userinfo.value?.age || '',
      height: profile.height || userinfo.value?.height || null,
      weight: profile.weight || userinfo.value?.weight || null,
      bmi: profile.bmi || userinfo.value?.bmi || '',
      user: profile.user_id || profile.phone_number || phone,
      phoneNumber: profile.phone_number || phone,
      customData: profile.customData || userinfo.value?.customData || {}
    }

    // birthday 规范化为 YYYY-MM-DD（复用现有 formatDateValue）
    normalized.birthday = formatDateValue(normalized.birthday)

    userinfo.value = { ...userinfo.value, ...normalized }
    return true
  } catch (e) {
    console.warn('[tryFetchUserInfoFromDeviceReport] failed:', e)
    return false
  }
}

// 抽取使用默认值的逻辑为单独的函数
const useDefaultUserInfo = () => {
  // 使用父组件传递的默认值
  const defaultUserInfo = props.selections?.userinfo;
  if (defaultUserInfo) {
    userinfo.value = { ...defaultUserInfo };
  } else {
    // 如果连默认值也没有，则清空所有字段
    userinfo.value = {
      username: '',
      sex: '',
      ethnicity: '',
      birthday: '',
      age: '',
      height: null,
      weight: null,
      bmi: '',
      customData: {}
    };
  }
}

// 添加日期格式化工具函数
const formatDateValue = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // 处理时间戳（假设是秒级时间戳）
    if (typeof dateValue === 'number') {
      const date = new Date(dateValue * 1000);
      return date.toISOString().split('T')[0];
    }
    
    // 处理 YYYY-MM-DD 格式的字符串
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }
    
    // 处理 YYYY-MM 格式的字符串
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}$/)) {
      return `${dateValue}-01`; // 假设日期为该月的第一天
    }
    
    // 处理其他日期字符串
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.error('日期格式化错误:', error);
    return '';
  }
};

// 在 onMounted 或 watch 中调用
onMounted(() => {
  if (props.device && props.date) {
    fetchReportInfo();
  }
})

// 监听设备和日期变化
watch([() => props.device, () => props.date, () => props.selections], ([newDevice, newDate, newSelections]) => {
  if (newDevice && newDate) {
    fetchReportInfo();
  }
  
  // 当 selections 更新时，更新 userinfo
  if (newSelections?.userinfo) {
    userinfo.value = {
      ...userinfo.value,
      ...newSelections.userinfo
    }
  }
}, { deep: true })

// 在 onMounted 时发出折叠事件
onMounted(() => {
  // 通知父组件折叠导航栏
  emit('view-report');
  
  if (props.device && props.date) {
    fetchReportInfo();
  }
});

// 定义 emit
const emit = defineEmits(['view-report']);

// 在日期改变时发射事件
const handleDateChange = (newDate) => {
  emit('date-change', newDate);
}

// 在日期选择器变化的地方调用这个方法
watch(() => selectedDate.value, (newDate) => {
  if (newDate) {
    handleDateChange(newDate);
  }
});

// 在 setup 函数中添加事件监听
onMounted(() => {
  // 添加事件监听
  emitter.on('questionnaire-updated', (data) => {
    if (data.deviceId === props.device && data.date === formatDate(selectedReportDate.value)) {
      fetchQuestionnaireScores()
    }
  })
})

// 在组件卸载时移除事件监听
onBeforeUnmount(() => {
  emitter.off('questionnaire-updated')
})
</script>

<style scoped>
.nap-tab-toggle {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 24px;
  padding: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.nap-tab-toggle button {
  min-width: 104px;
  padding: 8px 20px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.nap-tab-toggle button:hover {
  color: #2563eb;
  background: #eff6ff;
}

.nap-tab-toggle button.active {
  color: #ffffff;
  background: #2563eb;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.3);
}

.nap-report-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.nap-report-card {
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.nap-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f3f4f6;
}

.nap-card-header h2 {
  margin: 0 0 6px;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
}

.nap-card-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.nap-duration {
  flex: 0 0 auto;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f3f4f6;
  text-align: right;
}

.nap-duration span,
.nap-metric-item span {
  display: block;
  margin-bottom: 4px;
  color: #6b7280;
  font-size: 12px;
}

.nap-duration strong {
  color: #2563eb;
  font-size: 18px;
}

.nap-stage-section {
  margin-top: 20px;
}

.nap-section-title {
  margin-bottom: 10px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

.nap-stage-bar {
  display: flex;
  width: 100%;
  height: 14px;
  overflow: hidden;
  border-radius: 9999px;
  background: #f3f4f6;
}

.nap-stage-bar span {
  height: 100%;
  transition: width 0.25s ease;
}

.nap-stage-legend {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.nap-stage-legend > div {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #6b7280;
  font-size: 13px;
}

.nap-stage-legend strong {
  margin-left: auto;
  color: #374151;
  font-weight: 600;
}

.nap-stage-dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.nap-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.nap-metric-item {
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.nap-metric-item strong {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 640px) {
  .nap-tab-toggle {
    display: flex;
    width: 100%;
  }

  .nap-tab-toggle button {
    flex: 1;
    min-width: 0;
    padding-right: 12px;
    padding-left: 12px;
  }

  .nap-report-card {
    padding: 18px;
  }

  .nap-card-header {
    flex-direction: column;
  }

  .nap-duration {
    width: 100%;
    text-align: left;
  }

  .nap-stage-legend {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .nap-metrics {
    grid-template-columns: 1fr;
  }
}

/* ========== Taro H5 移动端视图手机壳 ========== */
/* ========== Taro H5 Dialog 内的手机壳 ========== */
/* Dialog 独立于外层容器, 尺寸可以稳定写死——这就是夏祯说"这样不用调任何东西"的原因 */
.phone-frame-dialog {
  position: relative;
  width: 360px;
  /* 视口小就按视口压; 外层 el-dialog 的 header/margin 约占 120-140px */
  height: min(720px, calc(100vh - 140px));
  min-height: 480px;
  aspect-ratio: 390 / 780;
  padding: 10px;
  border-radius: 40px;
  background: #111;
  box-shadow: 0 0 0 2px #333, 0 18px 48px rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
}
.phone-notch {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 126px;
  height: 28px;
  background: #111;
  border-radius: 0 0 18px 18px;
  z-index: 10;
  pointer-events: none;
}
.phone-screen {
  width: 100%;
  height: 100%;
  border-radius: 34px;
  overflow: hidden;
  background: #000;
}
.phone-screen iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

/* 自定义日历样式 */
:deep(.custom-calendar) {
  --vc-bg-transparent: transparent;
  --vc-border-radius: 0.5rem;
  --vc-font-family: inherit;
  --vc-text-gray-900: white;
  --vc-text-gray-400: rgba(255, 255, 255, 0.6);
  --vc-accent-600: rgb(37, 99, 235);
  --vc-accent-500: rgb(59, 130, 246);
  --vc-accent-400: rgb(96, 165, 250);
}

:deep(.vc-popover-content) {
  /* 弹出框样式 */
  @apply bg-white text-gray-900 shadow-lg;
}

:deep(.vc-day) {
  /* 日期单元格样式 */
  @apply hover:bg-blue-100/10;
}

:deep(.vc-day.is-disabled) {
  /* 禁用日期样式 */
  @apply opacity-50 cursor-not-allowed;
}

:deep(.report-dot) {
  /* 报告标记样式 */
  @apply w-2 h-2 rounded-full;
}

:deep(.vc-highlight) {
  /* 中日期样式 */
  @apply bg-blue-500/20;
}
.date-picker {
  @apply block w-full px-4 py-2 bg-white/10 border-0 rounded-lg text-white
  placeholder-white/60 focus:ring-2 focus:ring-white/40
  focus:bg-white/20 transition-all;
}

/* 添加一些动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 图表容器hover效果 */
.chart-container {
  @apply transition-all duration-300 ease-in-out;
}

.chart-container:hover {
  @apply transform scale-[1.01];
}

/* 添加图表容器的样式 */
.trend-chart-container {
  @apply bg-white rounded-lg p-4 border border-gray-100 hover:shadow-md transition-all;
}

/* 可以添加一些额外的样式来优化显示效果 */
.text-sm {
  @apply leading-tight;
}

.text-lg {
  @apply leading-snug;
}

@media print {
  /* 隐藏不必要的元素 */
  .no-print {
    display: none;
  }
  body {
    margin: 0;
    padding: 0;
    width: 210mm; /* A4 纸张宽度 */
  }
  /* 调整布局 */
  .print-container {
    margin: 0 auto;
    width: 100%;
    max-width: 190mm; /* 留出边 */
  }
  /* 确保颜显示 */
  .text-blue-500 {
    color: #1D4ED8 !important;
  }
  .bg-blue-100 {
    background-color: #DBEAFE !important;
  }
  .text-blue-500 {
    color: #1D4ED8 !important;
  }
  .bg-blue-100 {
    background-color: #DBEAFE !important;
  }
  .header {
    background-color: #1D4ED8 !important; /* 确保 header 栏颜色 */
    color: white !important;
  }
  .content {
    overflow: hidden;
  }

  /* 其他颜色调整 */
  /* 添加更多颜色调整以确保打印时显示 */
}

/* 基础样式 */
.report-page {
  width: 210mm;  /* A4 宽度 */
  min-height: 297mm;  /* A4 高度 */
  margin: 0 auto;
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  position: relative;
  padding: 20mm;
  box-sizing: border-box;
}

/* 图表容器样式 */
.chart-container {
  width: 170mm;  /* 210mm - 左右各20mm内边距 */
  margin: 0 auto;
}

/* echarts 图表样式 */
.echarts-wrapper {
  width: 100%;
  height: 300px;
  margin-bottom: 20px;
}

/* 打印样式 */
@media print {
  body {
    background: white;
    margin: 0;
    padding: 0;
  }

  .report-page {
    width: 210mm;
    min-height: 297mm;
    margin: 0;
    padding: 20mm;
    box-shadow: none;
    page-break-after: always;
  }

  .chart-container {
    width: 170mm;
  }

  /* 确保颜色打印 */
  .header {
    background-color: #1D4ED8 !important;
    color: white !important;
  }

  .text-blue-500 {
    color: #1D4ED8 !important;
  }

  .bg-blue-100 {
    background-color: #DBEAFE !important;
  }
}

/* 添加一些细微的动画效果 */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* 确保图标动画平滑 */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* 添加卡片阴影效果 */
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>